import React, { useState, useEffect } from "react";
import { Button } from "components/ui/button";
import { Badge } from "components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "components/ui/tabs";
import { format } from "date-fns";
import { ko } from "date-fns/locale";
import { apiGet, apiPost } from "components/Common/Axios";
import { Estimate, staticLectureClass } from "lib/types";
import { toast } from "sonner";
import SchoolSalesPopup from "../School/Main/SchoolSalesPopup";
import { SchoolSales, School } from "lib/models";
import CorrectSchoolNamePopup from "page/School/Main/CorrectSchoolNamePopup";
import { cn } from "lib/utils";
import { RootState, useAppSelector } from "store";

const EstimateListPage = () => {
  const [estimates, setEstimates] = useState<Estimate[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [activeTab, setActiveTab] = useState<string>("pending");
  const [pendingEstimates, setPendingEstimates] = useState<Estimate[]>([]);
  const [completedEstimates, setCompletedEstimates] = useState<Estimate[]>([]);
  const [school, setSchool] = useState<School | null>(null);
  const [schoolSales, setSchoolSales] = useState<SchoolSales | null>(null);

  const me = useAppSelector((state: RootState) => state.user.me);

  // SchoolSalesPopup 관련 상태
  const [isSchoolSalesPopupOpen, setIsSchoolSalesPopupOpen] =
    useState<boolean>(false);
  const [isCorrectSchoolNamePopupOpen, setIsCorrectSchoolNamePopupOpen] =
    useState<boolean>(false);
  const [selectedEstimate, setSelectedEstimate] = useState<Estimate | null>(
    null
  );

  // 견적 목록 조회
  const getEstimates = async () => {
    try {
      setLoading(true);
      const response = await apiGet("/estimate");

      if (response.error || !response.data) {
        console.log("견적 목록 조회 실패:", response.error);
        toast.error("견적 목록을 불러오는데 실패했습니다.");
        return;
      }

      setEstimates(response.data);
    } catch (error) {
      console.error("견적 목록 조회 오류:", error);
      toast.error("견적 목록을 불러오는데 실패했습니다.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getEstimates();
  }, []);

  useEffect(() => {
    setPendingEstimates(
      estimates.filter((estimate) => !estimate.isContractComplete)
    );
    setCompletedEstimates(
      estimates.filter((estimate) => estimate.isContractComplete)
    );
  }, [estimates]);

  // Estimate를 SchoolSales로 변환하는 함수
  const convertEstimateToSchoolSales = (estimate: Estimate): SchoolSales => {
    return {
      id: 0, // 새로운 SchoolSales이므로 0
      userId: me?.id ?? 0,
      schoolId: school?.id ?? 0,
      school: school ?? (null as any),
      stage: 4, // 견적요청 단계로 설정
      classType: estimate.classType,
      classTimes: estimate.classTimes,
      contractCnt: estimate.contractCnt,
      studentCnt: 0, // 기본값
      classDateFrom: estimate.classDate,
      classDateTo: estimate.classDate,
      contractDate: new Date(),
      startPeriod: 0,
      endPeriod: 0,
      grade: 0,
      calendarId: "",
      revenue: 0,
      revenueLecture: 0,
      revenueMaterial: 0,
      lectureFee: 0,
      etcFee: 0,
      isIssuedLecture: false,
      isIssuedMaterial: false,
      isIssuedSeparate: false,
      isFeedbackCompleted: false,
      isSendSms: false,
      cost: 0,
      isExclude33: false,
      profit: 0,
      isRevenueInputCompleted: false,
      isVatInvoiceRequested: false,
      createDate: new Date(),
      memo: "",
      teacherName: estimate.teacherName,
      phoneNo: estimate.teacherPhone,
      email: estimate.teacherEmail,
      vatInvoiceId: 0,
      vatInvoiceMaterialId: 0,
      user: null as any,
      vatInvoice: null as any,
      vatInvoiceMaterial: null as any,
      isFree: estimate.isFree,
    };
  };

  const getSchoolInfo = async (estimate: Estimate) => {
    try {
      const response = await apiPost("/school/search-one", {
        name: estimate.schoolName ?? "",
      });
      if (response.error && !response.data) {
        alert("학교 정보를 찾을 수 없습니다.");
        return null;
      }
      return response.data;
    } catch (error) {
      alert("학교 정보를 찾을 수 없습니다.");
      return;
    }
  };

  // 견적 행 클릭 핸들러
  const confirmSchoolName = async (estimate: Estimate) => {
    setSelectedEstimate(estimate);

    setIsCorrectSchoolNamePopupOpen(true);
  };

  // 영업팝업 오픈
  const openSchoolSalesPopup = async (estimate: Estimate) => {
    setSelectedEstimate(estimate);

    const school = await getSchoolInfo(estimate);
    setSchool(school);

    setSchoolSales(convertEstimateToSchoolSales(estimate));
    setIsSchoolSalesPopupOpen(true);
  };

  // 견적 Row 클릭시
  const handleEstimateRowClick = async (estimate: Estimate) => {
    // 학교이름을 확인했는지
    if (estimate.isConfirmSchool) {
      openSchoolSalesPopup(estimate);
    } else {
      confirmSchoolName(estimate);
    }
  };

  // SchoolSalesPopup 닫기 핸들러
  const handleSchoolSalesPopupClose = () => {
    setIsSchoolSalesPopupOpen(false);
    setSelectedEstimate(null);
  };

  // SchoolSalesPopup 저장 완료 핸들러
  const handleSchoolSalesSaveCompleted = async (
    sales: SchoolSales,
    isNewMode: boolean
  ): Promise<boolean> => {
    try {
      // 여기서 SchoolSales 저장 로직을 구현할 수 있습니다
      console.log("SchoolSales 저장:", sales, "새 모드:", isNewMode);
      toast.success("저장되었습니다.");
      return true;
    } catch (error) {
      console.error("SchoolSales 저장 오류:", error);
      toast.error("저장에 실패했습니다.");
      return false;
    }
  };

  // SchoolSalesPopup 삭제 핸들러
  const handleSchoolSalesDelete = async (
    sales: SchoolSales
  ): Promise<boolean> => {
    try {
      // 여기서 SchoolSales 삭제 로직을 구현할 수 있습니다
      console.log("SchoolSales 삭제:", sales);
      toast.success("삭제되었습니다.");
      return true;
    } catch (error) {
      console.error("SchoolSales 삭제 오류:", error);
      toast.error("삭제에 실패했습니다.");
      return false;
    }
  };

  if (loading) {
    return (
      <div className="w-full px-4 py-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">견적 목록을 불러오는 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full px-4 py-5 sm:px-6 lg:px-8 lg:py-6">
      <div className="w-full">
        <div>
          <div className="flex flex-col gap-3 pb-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <h1 className="text-xl font-bold tracking-tight text-gray-900 sm:text-2xl">
                견적 관리
              </h1>
              <p className="pt-1 text-sm text-gray-500">
                견적 요청 및 처리 현황을 관리합니다.
              </p>
            </div>
            <Button
              onClick={() => {
                window.location.href = "/estimate";
              }}
              className="bg-indigo-600 hover:bg-indigo-500"
            >
              새 견적 요청
            </Button>
          </div>

          <Tabs
            value={activeTab}
            onValueChange={setActiveTab}
            className="w-full"
          >
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="pending" className="flex items-center gap-2">
                <span>처리 대기</span>
                <Badge variant="secondary" className="ml-1">
                  {pendingEstimates.length}
                </Badge>
              </TabsTrigger>
              <TabsTrigger
                value="completed"
                className="flex items-center gap-2"
              >
                <span>처리 완료</span>
                <Badge variant="default" className="ml-1">
                  {completedEstimates.length}
                </Badge>
              </TabsTrigger>
            </TabsList>

            <TabsContent value="pending" className="space-y-4">
              <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-sm">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-50 text-gray-500 font-medium border-b border-gray-200 border-solid">
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                        견적 ID
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                        학교명
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                        수업 종류
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                        수업 시간
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                        학급수
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                        수업 날짜
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                        신청자
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                        연락처
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                        요청일
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                        학교명확인
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingEstimates.length === 0 ? (
                      <tr>
                        <td
                          colSpan={11}
                          className="px-4 py-10 text-center text-gray-500"
                        >
                          처리 대기 중인 견적이 없습니다.
                        </td>
                      </tr>
                    ) : (
                      pendingEstimates.map((estimate) => {
                        const lectureName =
                          staticLectureClass.find(
                            (lecture) => lecture.type === estimate.classType
                          )?.name || "알 수 없음";

                        return (
                          <tr
                            key={estimate.id}
                            className="border-b border-gray-200 border-solid hover:bg-gray-50 cursor-pointer"
                            onClick={() => handleEstimateRowClick(estimate)}
                          >
                            <td className="px-4 py-3 text-sm text-gray-900">
                              {estimate.id}
                            </td>
                            <td
                              className={cn(
                                "px-4 py-3 text-sm font-medium ",
                                estimate.isConfirmSchool
                                  ? "font-medium text-gray-900"
                                  : "font-normal text-gray-500"
                              )}
                            >
                              {estimate.schoolName}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700">
                              {lectureName}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700">
                              {estimate.classTimes}차시
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700">
                              {estimate.contractCnt}학급
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700">
                              {format(
                                new Date(estimate.classDate),
                                "yyyy년 MM월 dd일",
                                {
                                  locale: ko,
                                }
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">
                              {estimate.teacherName}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700">
                              {estimate.teacherPhone}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700">
                              {format(
                                new Date(estimate.requestEstimateDateTime),
                                "yyyy년 MM월 dd일",
                                {
                                  locale: ko,
                                }
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700 text-center">
                              {estimate.isConfirmSchool ? (
                                <Button
                                  variant="outline"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    confirmSchoolName(estimate);
                                  }}
                                >
                                  변경
                                </Button>
                              ) : (
                                "미확인"
                              )}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </TabsContent>

            <TabsContent value="completed" className="space-y-4">
              <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-sm">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-50 text-gray-500 font-medium border-b border-gray-200 border-solid">
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                        견적 ID
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                        학교명
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                        수업 종류
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                        수업 시간
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                        학급수
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                        수업 날짜
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                        신청자
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                        연락처
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                        요청일
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {completedEstimates.length === 0 ? (
                      <tr>
                        <td
                          colSpan={11}
                          className="px-4 py-10 text-center text-gray-500"
                        >
                          처리 완료된 견적이 없습니다.
                        </td>
                      </tr>
                    ) : (
                      completedEstimates.map((estimate) => {
                        const lectureName =
                          staticLectureClass.find(
                            (lecture) => lecture.type === estimate.classType
                          )?.name || "알 수 없음";

                        return (
                          <tr key={estimate.id} className="border-b border-gray-200 border-solid hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm text-gray-900">
                              {estimate.id}
                            </td>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">
                              {estimate.schoolName}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700">
                              {lectureName}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700">
                              {estimate.classTimes}차시
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700">
                              {estimate.contractCnt}학급
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700">
                              {format(
                                new Date(estimate.classDate),
                                "yyyy년 MM월 dd일",
                                {
                                  locale: ko,
                                }
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">
                              {estimate.teacherName}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700">
                              {estimate.teacherPhone}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700">
                              {format(
                                new Date(estimate.requestEstimateDateTime),
                                "yyyy년 MM월 dd일",
                                {
                                  locale: ko,
                                }
                              )}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* SchoolSalesPopup */}
      {selectedEstimate && (
        <>
          <SchoolSalesPopup
            open={isSchoolSalesPopupOpen}
            onOpenChange={setIsSchoolSalesPopupOpen}
            schoolSales={schoolSales ?? (undefined as any)}
            school={school ?? (null as any)}
            onClose={handleSchoolSalesPopupClose}
            onSaveCompleted={handleSchoolSalesSaveCompleted}
            onDelete={handleSchoolSalesDelete}
            isEnableNew={true}
            isShowDelete={false}
            accordionStates={[
              "item-1", // 기본 정보
              "item-2", // 수업 정보
              "item-3", // 계약 정보
              "item-4", // 견적 정보
              "item-5", // 세금계산서 정보
              "item-6", // 메모
            ]}
          />
          <CorrectSchoolNamePopup
            open={isCorrectSchoolNamePopupOpen}
            onOpenChange={setIsCorrectSchoolNamePopupOpen}
            schoolName={selectedEstimate.schoolName ?? ""}
            setSchoolName={(schoolName) => {
              setEstimates(
                estimates.map((estimate) =>
                  estimate.id === selectedEstimate?.id
                    ? {
                        ...estimate,
                        schoolName: schoolName,
                        isConfirmSchool: true,
                      }
                    : estimate
                )
              );
            }}
            estimateId={selectedEstimate.id}
          />
        </>
      )}
    </div>
  );
};

export default EstimateListPage;
